import { Injectable, ErrorHandler} from '@angular/core';
import { init, captureException } from '@sentry/browser';
import { environment } from 'src/environments/environment';

@Injectable()
export class SentryErrorHandler implements ErrorHandler {

  private readonly suppressedErrors = [
    'TenantNotFoundError',
    'TenantInfoUnavailableError'
  ];

  constructor() {
    if (environment.envName !== 'local' && navigator.cookieEnabled) {
      init({
        dsn: environment.sentryDSN,
        environment: environment.envName,
        ignoreErrors: [
          // Bare-`data` ReferenceError from injected Google tag (GTM/GA/Ads) code; never first-party (app GTM pushes use `window.dataLayer`).
          "Can't find variable: data",
          "data is not defined",
          "apstagLOADED is not defined",
          "ezDenty is not defined",
          "__IASOmidVerificationClient is not defined",
          "OMID is not defined", "omid is not defined",
          "IMGlobal is not defined",
          "moat_px is not defined",
          "IMUID is not defined",
          "Identifier 'listenerName' has already been declared",
          "undefined is not an object (evaluating 'n.features')",
          "Extension context invalidated.",
          'Not implemented on this platform',
          "fbq is not defined",
          // WebKit's password-manager autofill probing the login form as Safari
          // navigates away post-login; `_logicalFormForControl` is not ours (no
          // such symbol in @angular/forms or our code) and the form already submitted.
          /_logicalFormForControl\(.*\)\.formElement/,
          // Babel `_classCallCheck` noise from injected/extension scripts (and a
          // rare Popper.js path); the accompanying timeout comes from the same
          // non-first-party source. Suppressed by message since the stack frames
          // don't always carry an extension URL.
          'Cannot call a class as a function',
          /^operation timed out\.?$/i,
        ],
        beforeSend(event) {
          // Suppress third-party GA/GTM fetch-failure noise (CAMPDEV-7572):
          // an unhandled promise rejection that Sentry serializes to an empty "{}"
          // value, or whose breadcrumb trail ends in a failed GA/GTM request.
          const noiseException = event.exception?.values?.[0];
          if (noiseException) {
            // In @sentry/browser v4 the mechanism sits on event.exception; newer
            // SDKs place it on the individual value. Check both.
            const mechanismType =
              (event.exception as { mechanism?: { type?: string } })?.mechanism?.type ||
              (noiseException as { mechanism?: { type?: string } }).mechanism?.type;
            const isUnhandledRejection =
              noiseException.type === 'UnhandledRejection' ||
              mechanismType === 'onunhandledrejection';
            const rejectionValue = noiseException.value ?? '';
            const isEmptyRejection =
              !rejectionValue ||
              rejectionValue === '{}' ||
              rejectionValue === '[object Object]' ||
              // A rejection whose entire value is the DOM constructor name carries no
              // stack and no message. Injected tag/extension code rejects with these;
              // we never construct a CustomEvent anywhere in the apps or libs.
              /^"?CustomEvent"?$/.test(rejectionValue) ||
              /Non-Error promise rejection/i.test(rejectionValue);
            // GA4 posts /g/collect to analytics.google.com (and regional
            // google-analytics.com hosts); gtag also pings doubleclick.
            const analyticsHost = /googletagmanager\.com|google-analytics\.com|analytics\.google\.com|stats\.g\.doubleclick\.net/;
            // Only the final breadcrumb counts, as the comment above describes: a failed
            // GA call earlier in the trail says nothing about a later first-party
            // rejection, and GA is blocked for the whole session for ad-blocker users.
            const crumbs = event.breadcrumbs ?? [];
            const lastCrumb = crumbs[crumbs.length - 1];
            const lastCrumbUrl =
              typeof lastCrumb?.data?.['url'] === 'string' ? lastCrumb.data['url'] : '';
            const lastCrumbStatus = lastCrumb?.data?.['status_code'];
            const hasFailedAnalyticsBreadcrumb =
              analyticsHost.test(lastCrumbUrl) &&
              (lastCrumbStatus === 0 || lastCrumbStatus === undefined);
            if (isUnhandledRejection && (isEmptyRejection || hasFailedAnalyticsBreadcrumb)) {
              return null;
            }
          }
          if (event.exception?.values && Array.isArray(event.exception.values) && event.exception.values.length > 0) {
            const exception = event.exception.values[0];
            //ignore browser extensions error
            const extensionPatterns = [
              'chrome-extension://',
              'moz-extension://',
              'safari-web-extension://',
              'webkit-masked-url'
            ];

            // Check if the error originates from the Recorder Action extension
            const isRecorderError = event.exception.values.some(error =>
              error.stacktrace?.frames?.some(frame =>
                frame.filename?.includes("RecordActionTool") || frame.function?.includes("RecordActionTool")
              )
            );

            // Check if the error originates from any browser extension by scanning
            // both the exception message and the stack trace frame filenames/functions.
            const isBrowserExtensionError = extensionPatterns.some(pattern => {
              if (exception.value?.includes(pattern)) {
                return true;
              }
              return event.exception?.values?.some(error =>
                error.stacktrace?.frames?.some(frame =>
                  frame.filename?.includes(pattern) || frame.function?.includes(pattern)
                )
              );
            });

            if (exception.value) {
              const safeLinkCrawlersIgnore = exception.type === 'UnhandledRejection' && exception.value.includes('Object Not Found Matching Id:'); 
              const invalidProperties = [
                'loaded',
                'mvpConfig',
                'config',
                'get',
                'variables',
                'ns',
                'retrieveEnvelope',
                'outputCurrentConfiguration',
              ];
              const regex = new RegExp(`Cannot read properties of undefined \\(reading '(${invalidProperties.join('|')})'\\)`);

              if (regex.test(exception.value) || isBrowserExtensionError || isRecorderError || safeLinkCrawlersIgnore) {
                return null;
              }
            }
          }
          return event;
        }
      });
    }
  }

  handleError(error: any) {
    // Zone.js wraps unhandled promise rejections: error.rejection holds the original error
    const actualError = error.rejection || error.originalError || error;

    const isSuppressed = this.suppressedErrors.includes(actualError.name)
      || this.suppressedErrors.some(name => actualError.message?.includes(name));
    if (isSuppressed) {
      console.error(actualError);
      return;
    }

    if (environment.envName !== 'local' && navigator.cookieEnabled) {
      captureException(actualError);
    }
    if (!navigator.cookieEnabled) {
      alert('Cookies are blocked by your browser. Kindly enable cookies to continue.');
    }
    console.error(actualError);
  }
}
